/**
 * /video-compressor and /mp4-converter (SEO_BRIEF_11 section 2.1): a video in, an MP4 out, in
 * this tab, on the /burn-subtitles engine and the browsers it runs in.
 *
 * The page (tools/video_out.html) loads this bundle beside boot.js. It owns the drop zone, the
 * options, the progress line, the result and the Save button, and starts the worker
 * (`video-out-worker.ts`, where mediabunny lives) on the first drop, so a visitor who never picks
 * a video never downloads it. One worker per video: a new drop terminates the old one.
 *
 * The job is the root's `data-video-out`:
 *   compress  the video opens, the visitor picks how small and at what resolution, the page shows
 *             the size it expects (src/media/video-plan.ts `planCompression`), Compress makes it,
 *             and the result shows the size before and after.
 *   convert   the video opens and the MP4 is made straight away, repackaged when the picture is
 *             H.264 already and the sound is AAC or stays as it is ("Ready. Nothing was
 *             re-encoded."; src/media/video-plan.ts `ConvertPlan.remux`).
 * Save hands the MP4 over from a tap, which is what Safari on an iPhone needs to download it.
 *
 * Every word is in the template (`data-video-label`, the error and note paragraphs); this file
 * writes only numbers, sizes and file names.
 *
 * A file that won't open is named by its type when the page doesn't list that type (an AVI, an
 * old phone's 3GP in codecs no browser decodes): "We can't open AVI videos", with the ones that
 * work. A listed type that won't open is a damaged or mislabelled file, and gets the plain sentence.
 *
 * THE USAGE COUNTER (base/src/lib/usage.ts), under the root's `data-usage-tool` slug: "loaded"
 * once per video that opened, "failed" once per file that could not be read or made (or is not a
 * video), "downloaded" for each Save, as `mp4`. A video opened and never saved is the counter's
 * own `no_result` when the page is left.
 */

import {
  classify,
  extensionOf,
  mountDropzone,
  videoTypeName,
} from '../../base/src/lib/dropzone.ts';
import { clock, fileSize } from '../../base/src/lib/format.ts';
import { mediaFormat, usageCounter } from '../../base/src/lib/usage.ts';
import { VIDEO_OUT_READS } from '../../../../src/formats/video-out-tools.ts';
import {
  type CompressMode,
  type CompressPlan,
  candidates,
  compressedName,
  convertedName,
  EMAIL_MB,
  MB,
  planCompression,
  type Resolution,
  savedPercent,
  targetFromMb,
} from '../../../../src/media/video-plan.ts';
import { canBurnHere } from './lib/codec.ts';
import type {
  FromVideoWorker,
  ToVideoWorker,
  VideoFailure,
  VideoProbe,
} from './lib/video-protocol.ts';

type ErrorKey = VideoFailure | 'audio-file' | 'wrong-file' | 'video-format';

/** The types the page names (the rows' read formats): one of these that won't open is damaged. */
const NAMED: ReadonlySet<string> = new Set(VIDEO_OUT_READS);

function workerUrl(): string {
  const build = document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content.trim();
  return build
    ? `/static/build/burn/video-out-worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/burn/video-out-worker.js';
}

function q<T extends Element = HTMLElement>(root: ParentNode, sel: string): T | null {
  return root.querySelector<T>(sel);
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking in the same task cancels the download in some engines.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** "25 MB", as the visitor typed it: the target in the size the page names it in. */
function targetText(bytes: number): string {
  const mb = bytes / MB;
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

function mount(root: HTMLElement): void {
  const unsupported = q(document, '[data-video-unsupported]');
  if (!canBurnHere(globalThis as unknown as Record<string, unknown>)) {
    if (unsupported) unsupported.hidden = false;
    root.hidden = true;
    return;
  }

  const job = root.dataset['videoOut'] === 'compress' ? 'compress' : 'convert';
  const host = q(root, '[data-dropzone-host]');
  const fileLine = q(root, '[data-video-file]');
  const options = q(root, '[data-video-options]');
  const fitBox = q(root, '[data-video-fit]');
  const target = q<HTMLInputElement>(root, '[data-video-control="target"]');
  const email = q<HTMLButtonElement>(root, '[data-video-email]');
  const start = q<HTMLButtonElement>(root, '[data-video-start]');
  const working = q(root, '[data-video-working]');
  const bar = q(root, '[data-video-bar]');
  const fill = q(root, '[data-video-fill]');
  const pctSlot = q(root, '[data-video-working] [data-slot="pct"]');
  const cancel = q<HTMLButtonElement>(root, '[data-video-cancel]');
  const result = q(root, '[data-video-result]');
  const save = q<HTMLButtonElement>(root, '[data-video-save]');
  const status = q(root, '[data-video-status]');
  if (!host || !save) return;

  const usageTool = root.dataset['usageTool'] ?? '';
  const usage = usageTool ? usageCounter(usageTool) : null;
  const videoFormat = (file: File) => mediaFormat(extensionOf(file.name), 'video');

  const label = (key: string): string =>
    q(root, `[data-video-label="${key}"]`)?.textContent?.trim() ?? '';
  const fillSlots = (scope: ParentNode, name: string, text: string) => {
    for (const el of scope.querySelectorAll<HTMLElement>(`[data-slot="${name}"]`)) {
      el.textContent = text;
    }
  };

  let worker: Worker | null = null;
  let video: File | null = null;
  let probe: VideoProbe | null = null;
  let plan: CompressPlan | null = null;
  let busy = false;
  let done: { blob: Blob; name: string } | null = null;
  /** The size Fit under a size asked for when Compress was pressed: what the result is held to. */
  let asked: number | null = null;

  /* ── what the page shows ─────────────────────────────────────────────── */

  const showError = (key: ErrorKey | null) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-video-error]')) {
      if (el.dataset['videoError'] === 'target') continue;
      el.hidden = el.dataset['videoError'] !== key;
    }
  };
  const showNote = (keys: readonly string[]) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-video-note]')) {
      el.hidden = !keys.includes(el.dataset['videoNote'] ?? '');
    }
  };
  const setStatus = (text: string) => {
    if (status) status.textContent = text;
  };
  const setProgress = (fraction: number | null) => {
    if (bar) bar.dataset['indeterminate'] = fraction === null ? 'true' : 'false';
    const pct = fraction === null ? 0 : Math.max(0, Math.min(100, Math.floor(fraction * 100)));
    fill?.style.setProperty('--progress-pct', `${pct}%`);
    if (pctSlot) pctSlot.textContent = String(pct);
  };
  const setWorking = (on: boolean) => {
    busy = on;
    if (working) working.hidden = !on;
    if (on) setProgress(null);
    // The choices hold still while the video is made: the result is the one they asked for.
    for (const control of root.querySelectorAll<HTMLInputElement>('[data-video-control]')) {
      control.disabled = on;
    }
    if (email) email.disabled = on;
    refreshStart();
  };
  const clearResult = () => {
    done = null;
    save.hidden = true;
    save.disabled = true;
    if (result) result.hidden = true;
    showNote([]);
  };

  /* ── the compressor's choices ────────────────────────────────────────── */

  const chosen = (name: string): string =>
    q<HTMLInputElement>(root, `[data-video-control="${name}"]:checked`)?.value ?? '';
  const mode = (): CompressMode => {
    const m = chosen('mode');
    return m === 'much-smaller' || m === 'fit' ? m : 'smaller';
  };
  const resolution = (): Resolution => {
    const r = chosen('resolution');
    return r === '720' || r === '480' ? r : 'keep';
  };
  const targetBytes = (): number | null => targetFromMb(target?.value ?? '');

  /** Work the plan out again from the controls, and say what it expects. */
  const replan = () => {
    if (job !== 'compress') return;
    const m = mode();
    if (fitBox) fitBox.hidden = m !== 'fit';
    const bytes = targetBytes();
    const targetError = q(root, '[data-video-error="target"]');
    if (targetError) targetError.hidden = m !== 'fit' || bytes !== null;
    if (email) email.setAttribute('aria-pressed', String(m === 'fit' && bytes === EMAIL_MB * MB));
    plan = null;
    if (!probe || (m === 'fit' && bytes === null)) {
      showNote([]);
      refreshStart();
      return;
    }
    plan = planCompression(probe.facts, probe.ability, {
      mode: m,
      resolution: resolution(),
      ...(bytes !== null ? { targetBytes: bytes } : {}),
    });
    if (!plan) {
      showError('cannot-encode');
      showNote([]);
      refreshStart();
      return;
    }
    showError(null);
    fillSlots(root, 'estimate', fileSize(plan.estimatedBytes));
    if (bytes !== null) fillSlots(root, 'target', targetText(bytes));
    // The size the plan makes, by its short side: 720p, 480p, or a small video's own 360p.
    fillSlots(root, 'resolution', `${Math.min(plan.width, plan.height)}p`);
    // "To fit" only where the size asked for was the reason: the plan stepped below the first
    // size this browser can make (a phone that can't make 4K makes 720p in every mode).
    const first = candidates(probe.facts, resolution(), probe.ability.sizes)[0];
    if (m === 'fit' && plan.alreadyFits) showNote(['fits']);
    else if (m === 'fit' && !plan.reachable) showNote(['unreachable']);
    else if (m === 'fit' && plan.resolution !== first) showNote(['estimate', 'lowered']);
    else showNote(['estimate']);
    refreshStart();
  };

  function refreshStart(): void {
    if (!start) return;
    const nothingToDo = mode() === 'fit' && plan?.alreadyFits === true;
    start.disabled = busy || !probe || !plan || nothingToDo;
  }

  for (const control of root.querySelectorAll('[data-video-control]')) {
    control.addEventListener('change', replan);
    control.addEventListener('input', replan);
  }
  email?.addEventListener('click', () => {
    if (target) target.value = String(EMAIL_MB);
    const fit = q<HTMLInputElement>(root, '[data-video-control="mode"][value="fit"]');
    if (fit) fit.checked = true;
    replan();
  });

  /* ── the worker ──────────────────────────────────────────────────────── */

  const stopWorker = () => {
    worker?.terminate();
    worker = null;
  };

  /** A type the page doesn't name gets its own sentence, "We can't open AVI videos". */
  const notNamed = (file: File): boolean => {
    const ext = extensionOf(file.name);
    if (ext === '' || NAMED.has(ext)) return false;
    fillSlots(root, 'format', videoTypeName(file.name));
    return true;
  };

  /** Why this page can't work on an opened video, or null when it can. */
  const refusalOf = (opened: VideoProbe): ErrorKey | null => {
    // A picture in a codec nothing here knows (an old phone's H.263): no browser opens it.
    if (opened.videoCodec === null) return 'unreadable';
    // The compressor decodes every frame; the converter only what it re-encodes.
    const encodes = job === 'compress' || opened.convert.video === 'encode';
    if (encodes && !opened.canDecode) return 'cannot-decode';
    // The converter keeps the size; the compressor needs one size it can make (`replan` picks).
    const sizes = opened.ability.sizes;
    if (job === 'convert' && encodes && !sizes.keep) return 'cannot-encode';
    if (job === 'compress' && !(sizes.keep || sizes['720'] || sizes['480'])) return 'cannot-encode';
    return null;
  };

  const failed = (file: File, reason: ErrorKey, detail?: string) => {
    if (detail) console.warn('[video-out]', reason, detail);
    setWorking(false);
    usage?.failed(file, videoFormat(file));
    showError(reason === 'unreadable' && notNamed(file) ? 'video-format' : reason);
    setStatus('');
  };

  const onDone = (file: File, msg: Extract<FromVideoWorker, { type: 'done' }>) => {
    setWorking(false);
    const blob = new Blob([msg.buffer], { type: 'video/mp4' });
    const name = job === 'compress' ? compressedName(file.name) : convertedName(file.name);
    done = { blob, name };
    fillSlots(root, 'before', `${file.name} · ${fileSize(file.size)}`);
    let after = `${name} · ${fileSize(blob.size)}`;
    const notes: string[] = [];
    if (job === 'compress') {
      const percent = savedPercent(file.size, blob.size);
      if (percent > 0) {
        const smaller = q(root, '[data-video-label="smaller"]');
        if (smaller) {
          fillSlots(smaller, 'percent', String(percent));
          after += ` (${smaller.textContent?.trim() ?? ''})`;
        }
      } else {
        notes.push('not-smaller');
      }
      if (asked !== null && blob.size > asked) {
        fillSlots(root, 'after-size', fileSize(blob.size));
        fillSlots(root, 'target', targetText(asked));
        notes.push('over');
      }
    }
    fillSlots(root, 'after', after);
    if (result) result.hidden = false;
    showNote(notes);
    save.hidden = false;
    save.disabled = false;
    const parts = [label(msg.remuxed ? 'remuxed' : 'ready')];
    if (msg.audio === 'dropped') parts.push(label('audio-dropped'));
    setStatus(parts.join(' '));
  };

  const spawn = (file: File): Worker => {
    stopWorker();
    const w = new Worker(workerUrl(), { type: 'module' });
    worker = w;
    w.onmessage = (ev: MessageEvent<FromVideoWorker>) => {
      if (worker !== w) return;
      const msg = ev.data;
      if (msg.type === 'probed') {
        probe = msg.probe;
        const f = probe.facts;
        if (fileLine) {
          fileLine.hidden = false;
          fillSlots(
            fileLine,
            'file',
            `${file.name} · ${fileSize(file.size)} · ${clock(f.durationS)} · ${f.width} × ${f.height}`,
          );
        }
        const refusal = refusalOf(probe);
        if (refusal) {
          failed(file, refusal);
          return;
        }
        // Counted once the video can be worked on here; a refusal above counts as failed.
        usage?.loaded(file, videoFormat(file));
        if (job === 'compress') {
          if (options) options.hidden = false;
          replan();
        } else {
          run({ type: 'convert', file });
        }
      } else if (msg.type === 'progress') {
        setProgress(msg.fraction);
      } else if (msg.type === 'done') {
        onDone(file, msg);
      } else if (msg.type === 'failed') {
        if (msg.reason === 'cancelled') {
          setWorking(false);
          setStatus(label('cancelled'));
          return;
        }
        failed(file, msg.reason, msg.detail);
      }
    };
    w.onerror = (event) => {
      // A worker that failed to load (offline, blocked) answers like an unreadable file rather
      // than leaving the page on a progress bar.
      event.preventDefault();
      if (worker !== w) return;
      stopWorker();
      failed(file, busy ? 'failed' : 'unreadable');
    };
    return w;
  };

  const run = (message: Extract<ToVideoWorker, { type: 'compress' | 'convert' }>) => {
    clearResult();
    showError(null);
    setStatus('');
    asked = message.type === 'compress' && mode() === 'fit' ? targetBytes() : null;
    setWorking(true);
    worker?.postMessage(message satisfies ToVideoWorker);
  };

  const open = (file: File) => {
    clearResult();
    showError(null);
    setStatus('');
    setWorking(false);
    video = file;
    probe = null;
    plan = null;
    if (options) options.hidden = true;
    if (fileLine) fileLine.hidden = true;
    const w = spawn(file);
    // The converter's progress bar starts now: opening is the first part of its work.
    if (job === 'convert') setWorking(true);
    w.postMessage({ type: 'probe', file } satisfies ToVideoWorker);
  };

  /* ── input ───────────────────────────────────────────────────────────── */

  mountDropzone(host, {
    onFiles: (dropped) => {
      // One video at a time: the first one picked is the one worked on.
      const file = dropped[0];
      if (!file) return;
      const kind = classify(file);
      if (kind === 'container') {
        open(file);
        return;
      }
      stopWorker();
      clearResult();
      setWorking(false);
      setStatus('');
      if (kind === 'audio') {
        usage?.failed(file, mediaFormat(extensionOf(file.name), 'audio'));
        showError('audio-file');
      } else if (kind === 'other-video') {
        fillSlots(root, 'format', videoTypeName(file.name));
        usage?.failed(file, 'video');
        showError('video-format');
      } else {
        usage?.failed(file, kind === 'archive' ? 'zip' : 'unknown');
        showError('wrong-file');
      }
    },
  });

  start?.addEventListener('click', () => {
    if (!video || !plan || busy) return;
    if (!worker) spawn(video);
    run({ type: 'compress', file: video, plan });
  });

  // Stopping ends the worker outright, whatever it was doing: a Compress after it starts a new one.
  cancel?.addEventListener('click', () => {
    if (!busy) return;
    stopWorker();
    setWorking(false);
    setStatus(label('cancelled'));
  });

  save.addEventListener('click', () => {
    if (!done) return;
    saveBlob(done.blob, done.name);
    usage?.downloaded('mp4');
    setStatus(label('saved'));
  });
}

const root = document.querySelector<HTMLElement>('[data-video-out]');
if (root) mount(root);
